'use client';
import React from 'react';

import { SessionContext } from '@benzinga/session-context';
import { AuthenticationManager, User } from '@benzinga/session';
import { UserManager, UserManagerEvent } from '@benzinga/user-manager';
import { appEnvironment, appName } from '@benzinga/utils';

export const useUser = (): User | null => {
  const session = React.useContext(SessionContext);
  const [user, setUser] = React.useState<User | null>(null);

  const debounceGetAuthSession = React.useCallback(() => {
    const auth = session.getManager(AuthenticationManager).getAuthentication();

    if (auth && auth.user) {
      setUser(auth.user);
    } else {
      session
        .getManager(AuthenticationManager)
        .getAuthSession()
        .then(authSession => {
          if (authSession?.ok?.user) {
            setUser(authSession.ok.user);
          }
        });
    }
  }, [session]);

  React.useEffect(() => {
    const isProtoApp = appEnvironment().isApp(appName.proto);
    if (!isProtoApp) {
      debounceGetAuthSession();
    }
  }, [debounceGetAuthSession]);

  React.useEffect(() => {
    const userManager = session.getManager(UserManager);
    setUser(prev => userManager.getUser() ?? prev);
    const sub = userManager.subscribe((event: UserManagerEvent) => {
      if (event.type === 'user:user_update') {
        setUser(event.user ?? null);
      }
    });
    return () => sub.unsubscribe();
  }, [session]);

  return user;
};
